<template>
    <NavigationStatusCounter
        :opened="opened"
        :visible="isVisible"
        :title="counterTitle"
        :secondary-label="counterSecondaryLabel"
        :progress="counterProgress"
        :progress-bar-class="progressBarClass"
        :action-label="t('vue.subscribe_now')"
        @action="handleUpgrade"
    />
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import NavigationStatusCounter from "@/components/Navigation/NavigationStatusCounter.vue";

interface IProps {
    opened?: boolean;
    isTrialing?: boolean;
    isTrialExpired?: boolean;
    isSubscribed?: boolean;
    hasFreePlan?: boolean;
    totalDays?: number | null;
    defaultTrialDays?: number | null;
    usedDays?: number | null;
}

const props = withDefaults(defineProps<IProps>(), {
    opened: false,
    isTrialing: false,
    isTrialExpired: false,
    isSubscribed: false,
    hasFreePlan: false,
    totalDays: null,
    defaultTrialDays: null,
    usedDays: 0,
});

const emit = defineEmits<{
    upgrade: [];
}>();

const { t } = useTranslations();

const isVisible = computed<boolean>(() => {
    if (props.isTrialing === true) {
        return true;
    }

    return (
        props.isTrialExpired === true &&
        props.isSubscribed !== true &&
        props.hasFreePlan !== true
    );
});

const totalTrialDays = computed<number>(() =>
    normalizeNonNegativeNumber(props.totalDays ?? props.defaultTrialDays ?? 0),
);

const usedTrialDays = computed<number>(() =>
    normalizeNonNegativeNumber(props.usedDays ?? 0),
);

const daysLeft = computed<number>(() =>
    Math.max(0, totalTrialDays.value - usedTrialDays.value),
);

const progress = computed<number>(() => {
    const denominator = totalTrialDays.value > 0 ? totalTrialDays.value : 1;

    return Math.max(
        0,
        Math.min(100, (usedTrialDays.value / denominator) * 100),
    );
});

const counterProgress = computed<number>(() => {
    if (props.isTrialing === true) {
        return progress.value;
    }

    return 100;
});

const progressBarClass = computed<string>(() => {
    if (props.isTrialing === true) {
        return "bg-[#04C951]";
    }

    return "bg-[#FF0000]";
});

const counterTitle = computed<string>(() => {
    if (props.isTrialing !== true) {
        return t("vue.your_free_trial_expired");
    }

    return t("vue.free_trial_process_bar");
});

const counterSecondaryLabel = computed<string | null>(() => {
    if (props.isTrialing !== true) {
        return null;
    }

    return `${daysLeft.value} ${t("vue.days_left")} (${usedTrialDays.value}/${
        totalTrialDays.value
    } ${t("vue.days_used")})`;
});

const handleUpgrade = () => {
    emit("upgrade");
};

function normalizeNonNegativeNumber(value: number | null): number {
    const numberValue = Number(value ?? 0);

    if (Number.isFinite(numberValue) === false) {
        return 0;
    }

    return Math.max(0, numberValue);
}
</script>
